import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast, confirmDialog } from "@/lib/ui";
import { Check, X, Heart, MessageCircle } from "lucide-react";
import { Link } from "react-router-dom";

export default function Interests() {
  const [tab, setTab] = useState("received");
  const [received, setReceived] = useState([]);
  const [sent, setSent] = useState([]);
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      const [r, s] = await Promise.all([
        api.get("/matches/interests/received"),
        api.get("/matches/interests/sent"),
      ]);
      setReceived(r.data); setSent(s.data);
    } catch (e) { setErr(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const act = async (id, action) => {
    try {
      await api.post(`/matches/interests/${id}/${action}`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-7" data-testid="interests-page">
      <div className="eyebrow">Introduktioner</div>
      <h1 className="mt-2 font-serif text-4xl text-olive-700">Henvendelser</h1>
      <p className="mt-2 text-brown-600 text-sm max-w-2xl">
        Her ser du de introduktioner du har modtaget og sendt. Én accept åbner en privat samtale — indtil da bevares alt med respekt.
      </p>

      <div className="mt-6 inline-flex rounded-full border border-sand-300 p-1 bg-white">
        <TabBtn active={tab === "received"} onClick={() => setTab("received")} testid="tab-received">Modtaget</TabBtn>
        <TabBtn active={tab === "sent"} onClick={() => setTab("sent")} testid="tab-sent">Sendt</TabBtn>
      </div>

      {err && <div className="mt-6 text-destructive text-sm">{err}</div>}

      <div className="mt-8 space-y-4">
        {tab === "received" && received.length === 0 && <div className="text-brown-500 text-sm">Ingen modtagne introduktioner endnu.</div>}
        {tab === "received" && received.map((it) => (
          <div key={it.id} className="card-soft p-5" data-testid={`received-item-${it.id}`}>
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <div className="font-serif text-xl text-olive-700">
                  {it.sender_profile ? it.sender_profile.display_name : "Nogen"}
                </div>
                <div className="text-sm text-brown-600">
                  {it.sender_profile ? `${it.sender_profile.city || ""}${it.sender_profile.profession ? ` — ${it.sender_profile.profession}` : ""}` : ""}
                </div>
                <div className="text-xs mt-1 text-brown-500">Status: {statusLabel(it.status)}</div>
              </div>
              {it.status === "pending" ? (
                <div className="flex gap-2">
                  <button onClick={() => act(it.id, "accept")} className="btn-primary text-sm px-4 py-2" data-testid={`accept-${it.id}`}><Check className="h-4 w-4" /> Accepter</button>
                  <button onClick={() => act(it.id, "decline")} className="btn-secondary text-sm px-4 py-2" data-testid={`decline-${it.id}`}><X className="h-4 w-4" /> Afslå</button>
                </div>
              ) : it.status === "accepted" ? (
                <Link to="/chat" className="btn-primary text-sm px-4 py-2" data-testid={`open-chat-${it.id}`}><Heart className="h-4 w-4" /> Samtale</Link>
              ) : (
                <span className="text-xs text-brown-500 italic">{statusLabel(it.status)}</span>
              )}
            </div>
            {it.message && (
              <div className="mt-4 rounded-xl bg-olive-50 border border-olive-500/20 p-3 text-sm text-olive-900" data-testid={`msg-${it.id}`}>
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-olive-600 mb-1"><MessageCircle className="h-3 w-3" /> Introduktion</div>
                <div className="whitespace-pre-wrap">{it.message}</div>
              </div>
            )}
          </div>
        ))}

        {tab === "sent" && sent.length === 0 && <div className="text-brown-500 text-sm">Du har endnu ikke sendt nogen introduktioner.</div>}
        {tab === "sent" && sent.map((it) => (
          <div key={it.id} className="card-soft p-5" data-testid={`sent-item-${it.id}`}>
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <div className="font-serif text-xl text-olive-700">
                  Til {it.recipient_profile ? it.recipient_profile.display_name : "en modtager"}
                </div>
                <div className="text-sm text-brown-600">
                  {it.recipient_profile ? `${it.recipient_profile.city || ""}${it.recipient_profile.profession ? ` — ${it.recipient_profile.profession}` : ""}` : ""}
                </div>
                <div className="text-xs mt-1 text-brown-500">Status: {statusLabel(it.status)}</div>
              </div>
              <Link to={`/matches/${it.to_profile_id}`} className="btn-secondary text-sm px-4 py-2">Se profil</Link>
            </div>
            {it.message && (
              <div className="mt-4 rounded-xl bg-sand-100 border border-sand-300 p-3 text-sm text-brown-700" data-testid={`sent-msg-${it.id}`}>
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-brown-500 mb-1"><MessageCircle className="h-3 w-3" /> Din introduktion</div>
                <div className="whitespace-pre-wrap">{it.message}</div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function TabBtn({ children, active, onClick, testid }) {
  return (
    <button onClick={onClick} className={`px-5 py-2 rounded-full text-sm transition ${active ? "bg-olive-500 text-white" : "text-brown-600 hover:bg-sand-100"}`} data-testid={testid}>
      {children}
    </button>
  );
}

function statusLabel(s) {
  const map = { pending: "afventer", accepted: "accepteret", declined: "afslået", withdrawn: "trukket tilbage" };
  return map[s] || s;
}
